import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { createPath } from 'history';
import { getDocument } from '@jira/ssr__browser-globals/src/getDocument.tsx';
import { getLocation } from '@jira/ssr__browser-globals/src/getLocation.tsx';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';
import type { BrowserHistory, DynamicRouteBrowserHistory, Location } from '../../types.tsx';

type HistoryAction = 'POP' | 'PUSH' | 'REPLACE';
type Listener = (location: Location, action: HistoryAction) => void;

const methodsPlaceholders = {
	push: noop,
	replace: noop,
	goBack: noop,
	goForward: noop,
	listen: () => noop,
	block: () => noop,
	refreshRoutes: () => noop,
};

const getCurrentLocation = () => {
	const browserLocation = getLocation();
	// todo - don't force non-optional search and hash
	const { pathname = '', search = '', hash = '' } = browserLocation || {};

	return { pathname, search, hash };
};

const createLegacyListener = (updateExposedLocation: Listener, browserWindow: Window) => {
	const node = getDocument()?.querySelector('#content');
	const initialHref = getLocation()?.href;
	if (initialHref === undefined) {
		return () => noop;
	}
	let prevHref = initialHref;
	let listeners: Listener[] = [];
	const historyStack = [prevHref];
	// note: browser history length is capped at 50 entries

	let prevHistoryLength = browserWindow.history.length;
	let historyIndex = 0;
	const getAction = (currentHref: string): HistoryAction => {
		if (prevHistoryLength === browserWindow.history.length) {
			if (historyStack[historyIndex - 1] === currentHref) {
				historyIndex -= 1;

				return 'POP';
			}
			if (historyStack[historyIndex + 1] === currentHref) {
				historyIndex += 1;

				return 'POP';
			}
			historyStack[historyStack.length - 1] = currentHref;

			return 'REPLACE';
		}
		historyStack.push(currentHref);
		historyIndex += 1;

		prevHistoryLength = browserWindow.history.length;

		return 'PUSH';
	};
	const onNodeChanges = debounce(() => {
		const currentHref = getLocation()?.href;
		if (currentHref !== undefined && prevHref !== currentHref) {
			const newLocation = getCurrentLocation();
			const action = getAction(currentHref);
			updateExposedLocation(newLocation, action);

			listeners.forEach((listener) => listener(newLocation, action));

			prevHref = currentHref;
		}
	}, 50);

	if (node) {
		const observer = new MutationObserver(onNodeChanges);
		observer.observe(node, {
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		});
	}

	return (listener: any) => {
		listeners = listeners.concat(listener);

		return () => {
			listeners = listeners.filter((lst) => lst !== listener);
		};
	};
};

export const createLegacyHistory = (): BrowserHistory & DynamicRouteBrowserHistory => {
	const browserWindow = getWindow();
	let currentLocation = getCurrentLocation();
	let currentAction: HistoryAction = 'PUSH';
	const updateExposedLocation: Listener = (v, a) => {
		currentLocation = v;
		currentAction = a;
	};

	return {
		get location() {
			return currentLocation;
		},
		get length() {
			return browserWindow ? browserWindow.history.length : 1; // default length is 1
		},
		get action() {
			return currentAction;
		},
		...(browserWindow
			? {
					push: (path: string | Location) =>
						getLocation()?.assign(typeof path === 'string' ? path : createPath(path || {})),
					replace: (path: string | Location) => {
						const browserDocument = getDocument();
						if (browserDocument) {
							browserWindow.history.replaceState(
								{},
								browserDocument.title,
								typeof path === 'string' ? path : createPath(path || {}),
							);
						}
					},

					goBack: () => browserWindow.history.back(),

					goForward: () => browserWindow.history.forward(),
					listen: createLegacyListener(updateExposedLocation, browserWindow),
					block: () => noop,
					createHref: (location: Location) => createPath(location),
					refreshRoutes: () => noop,
				}
			: methodsPlaceholders),
	};
};
